import pick from 'lodash/pick';

import { fetchCurrentUser } from '../../ducks/user.duck';
import { createRecipientApi, destroyRecipientApi, retrieveRecipientApi } from '../../util/api';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import { getSubMerchantIdFromUser } from '../../util/genericHelpers';
import {
  buildInvalidPayoutDetailsSnapshot,
  buildPayoutDetailsSnapshot,
} from '../../util/payoutDetails';

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/StripePayoutPage/SET_INITIAL_VALUES';

export const CREATE_OMISE_RECIPIENT_REQUEST = 'app/BankDetailsPage/CREATE_OMISE_RECIPIENT_REQUEST';
export const CREATE_OMISE_RECIPIENT_SUCCESS = 'app/BankDetailsPage/CREATE_OMISE_RECIPIENT_SUCCESS';
export const CREATE_OMISE_RECIPIENT_ERROR = 'app/BankDetailsPage/CREATE_OMISE_RECIPIENT_ERROR';

export const SET_OMISE_RECIPIENT_DATA_REQUEST =
  'app/BankDetailsPage/SET_OMISE_RECIPIENT_DATA_REQUEST';
export const SET_OMISE_RECIPIENT_DATA_SUCCESS =
  'app/BankDetailsPage/SET_OMISE_RECIPIENT_DATA_SUCCESS';
export const SET_OMISE_RECIPIENT_DATA_ERROR = 'app/BankDetailsPage/SET_OMISE_RECIPIENT_DATA_ERROR';

export const DESTROY_OMISE_RECIPIENT_REQUEST =
  'app/BankDetailsPage/DESTROY_OMISE_RECIPIENT_REQUEST';
export const DESTROY_OMISE_RECIPIENT_SUCCESS =
  'app/BankDetailsPage/DESTROY_OMISE_RECIPIENT_SUCCESS';
export const DESTROY_OMISE_RECIPIENT_ERROR = 'app/BankDetailsPage/DESTROY_OMISE_RECIPIENT_ERROR';

// ================ Initial state ================ //

const initialState = {
  payoutDetailsSaveInProgress: false,
  payoutDetailsSaved: false,
  fromReturnURL: false,

  omiseRecipientInProgress: false,
  omiseRecipientCreated: false,
  omiseRecipientError: null,

  omiseRecipientData: null,
  omiseRecipientDataInProgress: false, // keep original key
  omiseRecipientDataError: null,
};

// ================ Reducer ================ //

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case SET_OMISE_RECIPIENT_DATA_REQUEST:
      return { ...state, omiseRecipientDataInProgress: true };
    case SET_OMISE_RECIPIENT_DATA_SUCCESS:
      return {
        ...state,
        omiseRecipientData: payload || null,
        omiseRecipientDataInProgress: false,
        omiseRecipientDataError: null,
      };
    case SET_OMISE_RECIPIENT_DATA_ERROR:
      return { ...state, omiseRecipientDataError: payload, omiseRecipientDataInProgress: false };

    case CREATE_OMISE_RECIPIENT_REQUEST:
      return {
        ...state,
        omiseRecipientInProgress: true,
        omiseRecipientCreated: false,
        omiseRecipientError: null,
      };
    case CREATE_OMISE_RECIPIENT_SUCCESS:
      return { ...state, omiseRecipientInProgress: false, omiseRecipientCreated: true };
    case CREATE_OMISE_RECIPIENT_ERROR:
      return { ...state, omiseRecipientInProgress: false, omiseRecipientError: payload };

    // NEW: destroy states
    case DESTROY_OMISE_RECIPIENT_REQUEST:
      return { ...state, omiseRecipientInProgress: true, omiseRecipientError: null };
    case DESTROY_OMISE_RECIPIENT_SUCCESS:
      return {
        ...state,
        omiseRecipientInProgress: false,
        omiseRecipientCreated: false,
        omiseRecipientData: null,
      };
    case DESTROY_OMISE_RECIPIENT_ERROR:
      return { ...state, omiseRecipientInProgress: false, omiseRecipientError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

const selectRecipientId = state =>
  state?.user?.currentUser?.attributes?.profile?.publicData?.omiseRecipientId;

const markStoredRecipientInvalid = async ({ dispatch, currentUser, recipientId, reason }) => {
  const existingPrivateData = currentUser?.attributes?.profile?.privateData || {};
  const payoutDetails = buildInvalidPayoutDetailsSnapshot(currentUser, { recipientId, reason });

  await dispatch(
    updateProfile({
      publicData: { omiseRecipientId: null },
      privateData: { ...existingPrivateData, payoutDetails },
    })
  );
};

export const createOmiseRecipientRequest = () => ({ type: CREATE_OMISE_RECIPIENT_REQUEST });
export const createOmiseRecipientSuccess = () => ({ type: CREATE_OMISE_RECIPIENT_SUCCESS });
export const createOmiseRecipientError = e => ({ type: CREATE_OMISE_RECIPIENT_ERROR, payload: e });

export const setOmiseRecipientDataRequest = () => ({ type: SET_OMISE_RECIPIENT_DATA_REQUEST });
export const setOmiseRecipientDataSuccess = recipient => ({
  type: SET_OMISE_RECIPIENT_DATA_SUCCESS,
  payload: recipient,
});
export const setOmiseRecipientDataError = e => ({
  type: SET_OMISE_RECIPIENT_DATA_ERROR,
  payload: e,
});

export const destroyOmiseRecipientRequest = () => ({ type: DESTROY_OMISE_RECIPIENT_REQUEST });
export const destroyOmiseRecipientSuccess = () => ({ type: DESTROY_OMISE_RECIPIENT_SUCCESS });
export const destroyOmiseRecipientError = e => ({
  type: DESTROY_OMISE_RECIPIENT_ERROR,
  payload: e,
});

// ================ Thunks ================ //

// Create or update recipient; then persist id to publicData
export const createOmiseRecipientForRefund = valuesArg => async (dispatch, getState) => {
  dispatch(createOmiseRecipientRequest());

  const state = getState();
  const existingRecipientId = selectRecipientId(state) || null;

  // Accept either FinalForm's shape ({ values }) or a plain object
  const values = valuesArg?.values ? valuesArg.values : valuesArg || {};

  const requestPayload = {
    ...values,
    recipientId: existingRecipientId,
  };

  try {
    const response = await createRecipientApi(requestPayload);

    const newRecipient = response?.recipient || null;
    const newId = newRecipient?.id;

    if (newRecipient) {
      dispatch(setOmiseRecipientDataSuccess(newRecipient));
    }

    const currentUser = state.user?.currentUser;
    const existingPublicData = currentUser?.attributes?.profile?.publicData || {};
    const existingPrivateData = currentUser?.attributes?.profile?.privateData || {};
    const recipientIdToStore = newId || existingRecipientId;
    const payoutDetailsSnapshot = buildPayoutDetailsSnapshot(values);

    if (recipientIdToStore) {
      payoutDetailsSnapshot.recipientId = recipientIdToStore;
    }

    const nextPublicData = recipientIdToStore
      ? { ...existingPublicData, omiseRecipientId: recipientIdToStore }
      : existingPublicData;
    const nextPrivateData = {
      ...existingPrivateData,
      payoutDetails: payoutDetailsSnapshot,
    };

    await dispatch(updateProfile({ publicData: nextPublicData, privateData: nextPrivateData }));
    await dispatch(fetchCurrentUser());

    dispatch(createOmiseRecipientSuccess());
    return newId;
  } catch (error) {
    console.error('Omise recipient creation/update failed:', error);
    dispatch(createOmiseRecipientError(error));
    throw error;
  }
};

// Retrieve recipient (if an id exists on the profile)
export const retrieveOmiseRecipient = () => async (dispatch, getState) => {
  const state = getState();
  const recipientId = selectRecipientId(state);

  if (!recipientId) return null;

  dispatch(setOmiseRecipientDataRequest());
  try {
    const subMerchantId = getSubMerchantIdFromUser(getState().user.currentUser);
    const response = await retrieveRecipientApi({ recipientId, subMerchantId });
    if (response?.recipientNotFound) {
      const currentUser = getState().user.currentUser;
      const reason = response?.reason || 'OMISE_RECIPIENT_NOT_FOUND';
      dispatch(setOmiseRecipientDataSuccess(null));
      try {
        await markStoredRecipientInvalid({ dispatch, currentUser, recipientId, reason });
        await dispatch(fetchCurrentUser());
      } catch (profileError) {
        console.warn('Failed to mark stale Omise recipient invalid:', profileError);
      }
      return null;
    }

    const recipient = response?.recipient || null;
    dispatch(setOmiseRecipientDataSuccess(recipient));
    return recipient;
  } catch (error) {
    dispatch(setOmiseRecipientDataError(error));
    return null;
  }
};

export const destroyOmiseRecipient = recipientId => async (dispatch, getState) => {
  dispatch(destroyOmiseRecipientRequest());
  try {
    await destroyRecipientApi({ recipientId });
    // Remove the linkage from the user's profile
    await dispatch(updateProfile({ publicData: { omiseRecipientId: null } }));
    await dispatch(fetchCurrentUser());

    // Clear local recipient data
    dispatch(setOmiseRecipientDataSuccess(null));
    dispatch(destroyOmiseRecipientSuccess());
    return true;
  } catch (error) {
    console.error('Destroy Omise recipient failed:', error);
    dispatch(destroyOmiseRecipientError(error));
    throw error;
  }
};

// Load user and (optionally) the recipient
export const loadData = () => async (dispatch, getState) => {
  dispatch(setInitialValues());

  const fetchCurrentUserOptions = {
    updateHasListings: false,
    updateNotifications: false,
  };

  const userResponse = await dispatch(fetchCurrentUser(fetchCurrentUserOptions));
  const omiseRecipientId = selectRecipientId(getState());

  if (omiseRecipientId) {
    await dispatch(retrieveOmiseRecipient());
  }

  return userResponse;
};
